import { useState } from 'react'
import type { match } from 'react-router-dom'
import { Col, Row, Card, Timeline, Typography, Button, Skeleton } from 'antd'
import { Flex, Heading } from '@weareberyl/design-system'
import { parseISO } from 'date-fns'
import { capitalize } from 'lodash'

import HeadTitle from '~/components/HeadTitle'
import { useSchemeProduct } from './data'
import {
  useProductConfigsFragment,
  useProductSummaryFragment,
  useProductVersionsFragment,
} from '~/gql/generated/graphql'
import { ConfigTable } from '../List/ConfigTable'
import ProductDetails from '../List/ProductDetails'
import { ProductPageHeader, ProductTypeTag } from '../components'
import CardSection from '~/components/shared/CardSection'
import {
  formatCap,
  formatDuration,
  isAMinutePass,
  isAPeriodPass,
  isASubscriptionPass,
  isBuyableProduct,
} from '../utils'

export const ProductPricingInfoPanels = ({ id }: { id: string }) => {
  const { complete, data } = useProductSummaryFragment({ id })
  const { data: pricingConfigData, complete: pricingComplete } =
    useProductConfigsFragment({ id })

  if (!complete) {
    return null
  }

  const isMinutePass = isAMinutePass(data.product_type)
  const isPeriodPass = isAPeriodPass(data.product_type)
  const isSubscriptionPass = isASubscriptionPass(data.product_type)
  const isBuyable = isBuyableProduct(data.product_type)

  const items1 = [
    isBuyable
      ? { key: 'Price', value: data.price.formatted_amount }
      : undefined,
    isMinutePass ? { key: 'Quantity', value: data.quantity } : undefined,
    {
      key: 'Discoverable',
      value: data.is_discoverable ? 'Discoverable' : 'Hidden',
    },
    {
      key: 'Protected',
      value: data.is_protected ? 'Protected' : 'Public',
    },
    {
      key: 'Active',
      value: data.is_active ? 'Active' : 'Inactive',
    },
    data.is_protected
      ? {
          key: 'Role allowlist',
          value: data.role_whitelist.join(','),
        }
      : undefined,
    isMinutePass
      ? {
          key: 'Rewards',
          value: data.fulfill_rewards.join(',') || '-',
        }
      : undefined,
    {
      key: 'OOB fine',
      value: data.waive_out_of_zone_parking_charge ? 'Waived' : 'Enforced',
    },

    isPeriodPass || isSubscriptionPass
      ? {
          key: 'Valid for',
          value: formatDuration(data.validity_interval) ?? '-',
        }
      : undefined,
    isPeriodPass
      ? {
          key: 'Availability',
          value: data.availability ? capitalize(data.availability) : '-',
        }
      : undefined,
    isPeriodPass || isSubscriptionPass
      ? { key: 'Journey cap', value: formatCap(data) ?? '-' }
      : undefined,
  ]

  const items2 = [
    {
      key: 'Out of bay fine (OOB)',
      value:
        data.scheme_accounting.scheme_pricing_configs.out_of_zone_parking_charge
          .formatted_amount,
    },
    {
      key: 'Out of zone fine (OOZ)',
      value:
        data.scheme_accounting.scheme_pricing_configs
          .out_of_service_area_parking_charge.formatted_amount,
    },
    data.scheme_accounting.scheme_pricing_configs
      .vehicle_not_permitted_in_zone_charge
      ? {
          key: 'Vehicle not permitted parking fine',
          value:
            data.scheme_accounting.scheme_pricing_configs
              .vehicle_not_permitted_in_zone_charge.formatted_amount,
        }
      : undefined,
    data.scheme_accounting.scheme_pricing_configs
      .undocked_in_zone_parking_charge
      ? {
          key: 'Undocked in zone fine (partially locked)',
          value:
            data.scheme_accounting.scheme_pricing_configs
              .undocked_in_zone_parking_charge.formatted_amount,
        }
      : undefined,
    data.scheme_accounting.scheme_pricing_configs.unlocked_vehicle_charge
      ? {
          key: 'Unlocked vehicle charge (flat fee)',
          value:
            data.scheme_accounting.scheme_pricing_configs
              .unlocked_vehicle_charge.formatted_amount,
        }
      : undefined,
    data.scheme_accounting.scheme_pricing_configs
      .rear_lock_unlocked_vehicle_charge
      ? {
          key: 'Rear lock - unlocked vehicle charge (flat fee)',
          value:
            data.scheme_accounting.scheme_pricing_configs
              .rear_lock_unlocked_vehicle_charge.formatted_amount,
        }
      : undefined,
  ]

  return (
    <>
      <Card
        title={
          <Flex justifyContent="space-between">
            <Heading flex={1}>
              {data.name}
              {data.is_active ? '' : ' (inactive)'}
            </Heading>
            <ProductTypeTag productType={data.product_type} />
          </Flex>
        }
      >
        <Flex gridGap={60}>
          <CardSection maxWidth={400} items={items1} />
          <CardSection maxWidth={400} items={items2} />
        </Flex>
      </Card>
      {pricingComplete && (
        <Card title="Vehicle pricing">
          <ConfigTable schemeProduct={pricingConfigData} />
        </Card>
      )}
    </>
  )
}

const ProductEditHistory = ({ id }: { id: string }) => {
  const { complete, data } = useProductVersionsFragment({ id })
  const [collapsed, setCollapsed] = useState(true)

  // Versions will be null for users without scheme_product.edit perms
  if (!complete || data.versions === null) {
    return null
  }

  const versions = collapsed ? data.versions?.slice(0, 5) : data.versions

  return (
    <Card title="Edit history">
      <Timeline mode="left">
        {versions?.map(version => {
          const datetime = parseISO(version.published_at)
          return (
            <Timeline.Item
              key={version.version_id}
              label={datetime.toLocaleString()}
            >
              <Typography.Title level={5}>{version.event}</Typography.Title>
              <Typography.Text ellipsis>
                {version.user?.email ?? version.user_id}
              </Typography.Text>
            </Timeline.Item>
          )
        })}
      </Timeline>
      {collapsed && data.versions && data.versions.length > 5 && (
        <Button onClick={() => setCollapsed(val => !val)}>Show more</Button>
      )}
    </Card>
  )
}

type Props = {
  match: match<{ id: string; scheme_id: string }>
}

const ProductDetail = ({ match }: Props) => {
  const schemeProductId = match.params.id
  const schemeId = match.params.scheme_id
  const { loading, data } = useSchemeProduct({ schemeProductId })

  return (
    <>
      <HeadTitle pageTitle={data?.scheme_product?.name ?? '...'} />
      <ProductPageHeader id={schemeProductId} schemeId={schemeId} goBack />

      <Skeleton loading={loading} active>
        {data?.scheme_product && (
          <Row gutter={16}>
            <Col xs={24} lg={16} className="flex-col">
              <ProductPricingInfoPanels id={schemeProductId} />
            </Col>
            <Col xs={24} lg={8} className="flex-col">
              {data.scheme_product.details && (
                <ProductDetails schemeProduct={data.scheme_product} />
              )}
              <ProductEditHistory id={schemeProductId} />
            </Col>
          </Row>
        )}
      </Skeleton>
    </>
  )
}

export default ProductDetail
